/**
 * Collection Variant Filter
 * Handles variant display logic using Shopify metafields:
 * - custom.exclude_color: list of colors to hide
 * - custom.exclude_size: sizes to hide per color (Format: "Herb - S (6–8), M (10–12)")
 */

class CollectionVariantFilter {
  constructor() {
    this.init();
    this.observeNewCards();
  }

  init() {
    document.querySelectorAll('product-card').forEach(card => {
      this.initializeCard(card);
    });
  }

  initializeCard(card) {
    if (card.dataset.variantFilterInit === 'true') return;
    card.dataset.variantFilterInit = 'true';

    const optionsContainer = card.querySelector('.product-card-options--container');
    if (!optionsContainer) return;

    const excludedSizesRaw = optionsContainer.dataset.excludedSizes || '';
    const excludedColorsRaw = optionsContainer.dataset.excludedColors || '';
    const currentColor = optionsContainer.dataset.currentColor || '';

    if (!excludedSizesRaw) return;

    const excludedSizesMap = this.parseExcludedSizes(excludedSizesRaw);
    const excludedColors = excludedColorsRaw.split(',').map(c => c.trim().toLowerCase()).filter(c => c);

    card._variantFilter = {
      excludedSizesMap,
      excludedColors,
      currentColor
    };

    // Apply size filtering for the current card's color
    if (currentColor) {
      this.filterSizesForColor(card, currentColor);
    }

    this.setupSwatchListeners(card);
  }

  parseExcludedSizes(raw) {
    // Format: "Charcoal black - S (6-8), M (10-12); Herb - L (14-16)"
    const map = new Map();
    if (!raw) return map;

    // Split by semicolon for multiple color entries
    const colorEntries = raw.split(';');
    colorEntries.forEach(entry => {
      entry = entry.trim();
      if (!entry) return;

      // Find the first " - " to split color from sizes
      const dashIndex = entry.indexOf(' - ');
      if (dashIndex === -1) return;

      const color = entry.substring(0, dashIndex).trim().toLowerCase();
      const sizesStr = entry.substring(dashIndex + 3).trim();
      const sizes = sizesStr.split(',').map(s => s.trim()).filter(s => s);

      if (!map.has(color)) {
        map.set(color, []);
      }
      map.get(color).push(...sizes);
    });

    return map;
  }

  filterSizesForColor(card, colorName) {
    const filter = card._variantFilter;
    if (!filter) return;

    const colorLower = colorName.toLowerCase().trim();
    const excludedSizes = filter.excludedSizesMap.get(colorLower) || [];

    const sizeElements = card.querySelectorAll('.product-card-sizes--size');
    sizeElements.forEach(sizeEl => {
      const sizeValue = (sizeEl.dataset.size || '').trim();
      const sizeClean = (sizeEl.dataset.sizeClean || '').trim();
      
      const isExcluded = excludedSizes.some(excSize => {
        const excSizeLower = excSize.toLowerCase().trim();
        const sizeValueLower = sizeValue.toLowerCase();
        const sizeCleanLower = sizeClean.toLowerCase();
        
        // Extract clean size from excluded (e.g., "S (6-8)" -> "S")
        const excSizeClean = excSizeLower.split('(')[0].trim();
        
        // Exact match on full value
        if (sizeValueLower === excSizeLower) return true;
        
        // Exact match on clean size (e.g., "S" matches "S (6-8)")
        if (sizeCleanLower === excSizeLower) return true;
        
        // Exact match: clean size from excluded matches clean size from current
        if (sizeCleanLower === excSizeClean) return true;
        
        return false;
      });

      if (isExcluded) {
        sizeEl.classList.add('size-excluded');
        sizeEl.style.display = 'none';
      } else {
        sizeEl.classList.remove('size-excluded');
        sizeEl.style.display = '';
      }
    });

    this.updateVariantAvailability(card, colorName, excludedSizes);
  }

  updateVariantAvailability(card, colorName, excludedSizes) {
    const variantDataEl = card.querySelector('.variant-data, [type="application/json"]');
    if (!variantDataEl) return;

    try {
      const variants = JSON.parse(variantDataEl.textContent);
      const colorLower = colorName.toLowerCase();

      variants.forEach(variant => {
        if (!variant.options) return;

        const colorOptionIndex = variant.options.findIndex((opt) => {
          const optLower = (opt || '').toLowerCase();
          return optLower === colorLower;
        });

        if (colorOptionIndex === -1) return;

        variant.options.forEach((opt, idx) => {
          if (idx === colorOptionIndex) return;
          
          const optLower = (opt || '').toLowerCase();
          const optClean = optLower.split('(')[0].trim();
          const isExcluded = excludedSizes.some(excSize => {
            const excSizeLower = excSize.toLowerCase().trim();
            const excSizeClean = excSizeLower.split('(')[0].trim();
            return optLower === excSizeLower || optClean === excSizeLower || optClean === excSizeClean;
          });

          if (isExcluded) {
            variant._excluded = true;
          }
        });
      });
    } catch (e) {
      // Silently fail
    }
  }

  setupSwatchListeners(card) {
    const swatches = card.querySelectorAll('.product-card-swatch');
    swatches.forEach(swatch => {
      swatch.addEventListener('mouseover', () => {
        const colorName = swatch.dataset.color || swatch.querySelector('span')?.textContent || '';
        if (colorName) {
          this.filterSizesForColor(card, colorName);
        }
      });

      swatch.addEventListener('click', () => {
        const colorName = swatch.dataset.color || swatch.querySelector('span')?.textContent || '';
        if (colorName) {
          this.filterSizesForColor(card, colorName);
        }
      });
    });
  }

  observeNewCards() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.tagName === 'PRODUCT-CARD') {
              this.initializeCard(node);
            }
            node.querySelectorAll?.('product-card').forEach(card => {
              this.initializeCard(card);
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.collectionVariantFilter = new CollectionVariantFilter();
  });
} else {
  window.collectionVariantFilter = new CollectionVariantFilter();
}

document.addEventListener('pagination:page-changed', () => {
  if (window.collectionVariantFilter) {
    window.collectionVariantFilter.init();
  }
});
